class SimpleMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.sm-track');
    if (!this.track) return;

    // Clone all items once — track becomes 2× wide for seamless loop
    Array.from(this.track.children).forEach(item => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Wait for all images to load before measuring scrollWidth.
    // Safari returns 0 if images haven't been decoded/laid out yet.
    const images = Array.from(this.track.querySelectorAll('img'));
    const pending = images.filter(img => !img.complete);

    if (pending.length === 0) {
      requestAnimationFrame(() => this.start());
    } else {
      Promise.all(
        pending.map(img => new Promise(resolve => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }))
      ).then(() => requestAnimationFrame(() => this.start()));
    }
  }

  start() {
    const trackWidth = this.track.scrollWidth / 2;
    if (!trackWidth) return;

    this.trackWidth = trackWidth;

    const speed    = parseFloat(this.dataset.speed) || 80;
    const duration = trackWidth / speed;
    const reversed = this.dataset.reversed === 'true';

    // fromTo is explicit about start/end so Safari can't misread the initial value
    this.tween = gsap.fromTo(
      this.track,
      { x: reversed ? -trackWidth : 0 },
      { x: reversed ? 0 : -trackWidth, ease: 'none', repeat: -1, duration, force3D: true }
    );

    this.setupManualScroll();
  }

  setupManualScroll() {
    let resumeTimer;

    const nudge = (dx) => {
      this.tween.pause();
      clearTimeout(resumeTimer);

      let newX = gsap.getProperty(this.track, 'x') + dx;
      // Wrap for seamless loop: keep in range [-trackWidth, 0]
      newX = ((newX % this.trackWidth) - this.trackWidth) % this.trackWidth;
      gsap.set(this.track, { x: newX });

      // Resume auto-scroll after a pause
      resumeTimer = setTimeout(() => {
        const progress = Math.abs(gsap.getProperty(this.track, 'x')) / this.trackWidth;
        this.tween.progress(progress % 1).play();
      }, 1500);
    };

    // Only hijack wheel for horizontal gestures (trackpad horizontal swipe or shift+wheel).
    // Pure vertical wheel passes through so the page can scroll between sections.
    this.addEventListener('wheel', e => {
      const horizontal = e.shiftKey ? e.deltaY : e.deltaX;
      if (Math.abs(horizontal) <= Math.abs(e.deltaY) && !e.shiftKey) return;
      nudge(-horizontal);
      e.preventDefault();
    }, { passive: false });

    // Touch swipe
    let touchStartX = 0;
    let lastTouchX = 0;

    this.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      lastTouchX = touchStartX;
    }, { passive: true });

    this.addEventListener('touchmove', e => {
      const currentX = e.touches[0].clientX;
      const dx = currentX - lastTouchX;
      lastTouchX = currentX;
      // Only hijack if swiping mostly horizontally
      if (Math.abs(currentX - touchStartX) > 10) {
        nudge(dx);
        e.preventDefault();
      }
    }, { passive: false });
  }

  disconnectedCallback() {
    if (this.tween) this.tween.kill();
  }
}

customElements.define('simple-marquee', SimpleMarquee);
